import { Typography, Descriptions, Button, Card, Modal, Select, notification } from 'antd';
import { format } from 'date-fns';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import GachaInfoDescriptions, { GameInfoDescriptionItemType } from '@/Gacha/components/GachaInfoDescriptions';
import GachaInfoDescriptionsAggregatedData from '@/Gacha/components/GachaInfoDescriptionsAggregatedData';
import { EmailTemplate } from '@/api-client/generated/models';
import { useGacha } from '@/hooks/context/GachaContext';
import { createApiClientWithToken } from '@/service/PreRegistrationApiFactory';
import '@/Gacha/styles/showGachaDetail.css';
import { convertRateToPercentage } from '@/service/numericOperation';

const dateFormat = 'yyyy/MM/dd HH:mm:ss';

const gachaSettingInfoItemsType: { [key: string]: GameInfoDescriptionItemType } = {
  id: {
    label: 'ガチャID',
  },
  name: {
    label: 'ガチャ名',
  },
  upperTimes: {
    label: '上限回数',
  },
  winningRate: {
    label: '当選確率 (%)',
    render: (winningRate: number) => `${convertRateToPercentage(winningRate)} %`,
  },
  gachaTimes: {
    label: '回された回数',
  },
  iosDownloadUrl: {
    label: 'iOS ダウンロード URL',
  },
  androidDownloadUrl: {
    label: 'Android ダウンロード URL',
  },
  canRetry: {
    label: '引き直し可能か',
    render: (canRetry: boolean) => (canRetry ? '⭕️可能' : '✖︎不可能'),
  },
  retryInterval: {
    label: '引き直し可能なまでの時間(時間）',
    render: (v) => (v / 60 / 60).toString(),
  },
  createdAt: {
    label: '作成日',
    render: (v) => format(v, dateFormat),
  },
  startTime: {
    label: '開始日',
    render: (v) => format(v, dateFormat),
  },
  endTime: {
    label: '終了日',
    render: (v) => format(v, dateFormat),
  },
};

const enduserInfoItemsType: { [key: string]: GameInfoDescriptionItemType } = {
  gameTitle: {
    label: 'ゲームタイトル',
  },
  developerName: {
    label: 'デベロッパー名',
  },
  iconImageUrl: {
    label: 'アイコン画像URL',
  },
  giftDetail: {
    label: 'ギフトコード内容',
  },
  gameIntroduction: {
    label: 'ゲーム紹介文',
  },
  articleUrl: {
    label: '記事URL',
  },
  gameReleaseTime: {
    label: 'ゲームリリース時期',
  },
  officialSiteUrl: {
    label: '公式サイトURL',
  },
};

const ShowGachaDetail: React.FC<{}> = () => {
  const api = createApiClientWithToken();
  const { gacha } = useGacha();
  const [registrationEmail, setRegistrationEmail] = useState<EmailTemplate>({ htmlBody: '' });
  const [releaseEmail, setReleaseEmail] = useState<EmailTemplate>({ htmlBody: '' });
  const [releaseEmailOs, setReleaseEmailOs] = useState<string>('');
  const [visibleSendReleaseEmailModal, setVisibleSendReleaseEmailModal] = useState<boolean>(false);

  useEffect(() => {
    api.findRegistrationEmailTemplate(gacha.id).then((res) => {
      setRegistrationEmail(res);
    });

    api.findReleaseEmailTemplate(gacha.id).then((res) => {
      setReleaseEmail(res);
    });
  }, []);

  const EditButton = (
    <Button type="primary">
      <Link type="primary" to={{ pathname: `/gacha/${gacha.id}/update` }}>
        データを変更する
      </Link>
    </Button>
  );

  const openSendReleaseEmailModal = () => {
    setVisibleSendReleaseEmailModal(true);
  };

  const hideSendReleaseEmailModal = () => {
    setVisibleSendReleaseEmailModal(false);
  };

  const selectOs = (value) => {
    setReleaseEmailOs(value);
  };

  const sendReleaseEmail = () => {
    api
      .sendReleaseEmail(gacha.id, {
        os: releaseEmailOs === 'all' ? '' : releaseEmailOs,
      })
      .then(() => {
        notification.success({
          message: 'リリース通知のメールの送信に成功しました',
        });
      })
      .catch(() => {
        notification.error({
          message: 'リリース通知の送信に失敗しました',
          description: 'エンジニアまで確認をお願いします',
        });
      })
      .finally(() => {
        hideSendReleaseEmailModal();
      });
  };

  if (!gacha) {
    return <div>Loading</div>;
  }

  return (
    <div>
      <Card title={<Typography.Title level={4}>ガチャの詳細画面</Typography.Title>} extra={EditButton}>
        <GachaInfoDescriptions title="管理設定情報" gacha={gacha} itemsType={gachaSettingInfoItemsType} />
        <br />
        <GachaInfoDescriptions title="エンドユーザー向け情報" gacha={gacha} itemsType={enduserInfoItemsType} />
        <br />
        <GachaInfoDescriptionsAggregatedData gacha={gacha} />
        <br />
        <Descriptions title="登録完了メール" column={1} bordered layout="vertical">
          <Descriptions.Item label="件名">{registrationEmail.title}</Descriptions.Item>
          <Descriptions.Item label="本文" className="mail-body">
            {registrationEmail.body}
          </Descriptions.Item>
          <Descriptions.Item label="本文（HTML）" className="mail-body">
            {registrationEmail.htmlBody}
          </Descriptions.Item>
        </Descriptions>
        <br />
        <Descriptions
          title="リリース通知メール"
          column={1}
          bordered
          layout="vertical"
          extra={<Button onClick={openSendReleaseEmailModal}>リリース通知を送る</Button>}
        >
          <Descriptions.Item label="件名">{releaseEmail.title}</Descriptions.Item>
          <Descriptions.Item label="本文" className="mail-body">
            {releaseEmail.body}
          </Descriptions.Item>
          <Descriptions.Item label="本文（HTML）" className="mail-body">
            {releaseEmail.htmlBody}
          </Descriptions.Item>
        </Descriptions>
      </Card>
      <Modal
        visible={visibleSendReleaseEmailModal}
        onCancel={hideSendReleaseEmailModal}
        onOk={sendReleaseEmail}
        okButtonProps={{ disabled: !releaseEmailOs }}
      >
        <p>リリース通知を行う OS を選択してください</p>
        <Select defaultValue="選択してください" onChange={selectOs}>
          <Select.Option value="" disabled>
            選択してください
          </Select.Option>
          <Select.Option value="all">iOS・Android 両方</Select.Option>
          <Select.Option value="ios">iOS</Select.Option>
          <Select.Option value="android">Android</Select.Option>
        </Select>
        <p>OK を押してリリース通知のメールを送信します。</p>
      </Modal>
    </div>
  );
};

export default ShowGachaDetail;
